'use client';

import React from 'react';

export const AlertDialogContext = React.createContext<
  <T extends AlertAction>(
    params: T,
  ) => Promise<T['type'] extends 'alert' | 'confirm' ? boolean : null | string>
  // eslint-disable-next-line @typescript-eslint/no-non-null-assertion
>(() => null!);

export type AlertAction =
  | { type: 'alert'; title: string; body?: React.ReactNode; cancelButton?: string }
  | {
      type: 'confirm';
      title: string;
      body?: React.ReactNode;
      cancelButton?: string;
      actionButton?: string;
      /** The action removes or discards something: the button is styled destructive. */
      destructive?: boolean;
    }
  | {
      type: 'prompt';
      title: string;
      body?: React.ReactNode;
      cancelButton?: string;
      actionButton?: string;
      defaultValue?: string;
      /**
       * Keep line breaks: Enter adds a line, the action button sends. Off, the field still
       * wraps and grows with its text, but Enter sends and pasted breaks become spaces.
       */
      multiline?: boolean;
      inputProps?: React.TextareaHTMLAttributes<HTMLTextAreaElement>;
    }
  | { type: 'close' };
type Params<T extends 'alert' | 'confirm' | 'prompt'> =
  | Omit<Extract<AlertAction, { type: T }>, 'type'>
  | string;

export function useConfirm() {
  const dialog = React.useContext(AlertDialogContext);

  return React.useCallback(
    (params: Params<'confirm'>) => {
      return dialog({
        ...(typeof params === 'string' ? { title: params, body: undefined } : params),
        type: 'confirm',
      });
    },
    [dialog],
  );
}
export function usePrompt() {
  const dialog = React.useContext(AlertDialogContext);

  return (params: Params<'prompt'>) =>
    dialog({
      ...(typeof params === 'string' ? { title: params, body: undefined } : params),
      type: 'prompt',
    });
}
export function useAlert() {
  const dialog = React.useContext(AlertDialogContext);
  return (params: Params<'alert'>) =>
    dialog({
      ...(typeof params === 'string' ? { title: params, body: undefined } : params),
      type: 'alert',
    });
}
